import { Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import { generateTags } from "@/lib/articleTags";
import { trackEventBeacon } from "@/lib/api";

const SKOOL_URL = "https://www.skool.com/glp-1-lab-os-1374";

// Priority order: nausea > constipation > fatigue > plateau > muscle > food/protein > default
const TAG_CTA_MAP = [
  {
    tags: ["nausea"],
    guide: "eat-without-feeling-sick",
    copy: "Struggling with GLP-1 nausea? This guide walks you through the exact eating protocol.",
  },
  {
    tags: ["constipation"],
    guide: "fix-constipation",
    copy: "Managing GLP-1 constipation? Get the structured protocol.",
  },
  {
    tags: ["fatigue"],
    guide: "fix-fatigue",
    copy: "Low energy on GLP-1? Here is the protocol that addresses the root cause.",
  },
  {
    tags: ["plateau", "weight-loss"],
    guide: "break-plateau",
    copy: "Hit a plateau? Start with this.",
  },
  {
    tags: ["muscle-loss", "protein"],
    guide: "stop-losing-muscle",
    copy: "Protecting muscle on GLP-1 starts with this guide.",
  },
  {
    tags: ["food", "meal-timing"],
    guide: "eat-without-feeling-sick",
    copy: "Eating on GLP-1 is different. Here is what actually works.",
  },
];

function getTopicCTA(article, guides) {
  const articleTags = generateTags(article);
  for (const mapping of TAG_CTA_MAP) {
    if (mapping.tags.some(t => articleTags.includes(t))) {
      const guide = guides[mapping.guide];
      if (guide) return { guide, copy: mapping.copy };
    }
  }
  return null;
}

// Soft, educational mid-article CTA — topic-aware
export const MidArticleCTA = ({ tool, freebie, article, guides }) => {
  const topicCTA = article && guides ? getTopicCTA(article, guides) : null;

  if (topicCTA) {
    return (
      <div className="my-10 p-5 bg-[#E9EDC9]/60 rounded-2xl border-l-4 border-[#2D6A4F]" data-testid="mid-article-cta">
        <p className="text-base text-[#1A2018] mb-1 font-medium">
          {topicCTA.copy}
        </p>
        <Link
          to={`/guides/${topicCTA.guide.slug}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D6A4F] hover:text-[#1B4332] transition-colors mt-2"
        >
          Get the guide — ${topicCTA.guide.price} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  if (!tool && !freebie) return null;

  return (
    <div className="my-10 p-5 bg-[#E9EDC9]/60 rounded-2xl border-l-4 border-[#2D6A4F]" data-testid="mid-article-cta">
      {tool && (
        <>
          <p className="text-base text-[#1A2018] mb-1 font-medium">
            Still navigating this?
          </p>
          <p className="text-sm text-[#4B5548] mb-3">
            Use the free {tool.title} to get a clearer picture of your situation and personalized educational insights.
          </p>
          <Link
            to={`/${tool.slug}`}
            onClick={() =>
              trackEventBeacon("article_to_tool_clicked", {
                article_slug: article?.slug || null,
                tool_id: tool.id || null,
                placement: "mid_article",
              })
            }
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D6A4F] hover:text-[#1B4332] transition-colors"
          >
            Try the free assessment <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </>
      )}
      {!tool && freebie && (
        <>
          <p className="text-base text-[#1A2018] mb-1 font-medium">
            Want a quick reference for this?
          </p>
          <p className="text-sm text-[#4B5548] mb-3">
            {freebie.description}
          </p>
          <Link
            to={`/free/${freebie.slug}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D6A4F] hover:text-[#1B4332] transition-colors"
          >
            Get the free {freebie.title} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </>
      )}
    </div>
  );
};

// Soft, educational end-of-article CTA — topic-aware
export const EndArticleCTA = ({ guide, conclusion, article, guides }) => {
  const topicCTA = article && guides ? getTopicCTA(article, guides) : null;
  const ctaGuide = topicCTA ? topicCTA.guide : guide;

  return (
    <>
      <div className="mt-12 p-6 bg-[#2D6A4F] rounded-2xl text-white" data-testid="end-article-cta">
        {conclusion && (
          <p className="text-base text-white/90 mb-4 leading-relaxed">{conclusion}</p>
        )}
        {topicCTA && (
          <div className="pt-4 border-t border-white/20">
            <p className="text-sm text-white/70 mb-3">{topicCTA.copy}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to={`/guides/${ctaGuide.slug}`}
                className="px-4 py-2.5 bg-white/15 hover:bg-white/25 rounded-xl text-sm font-medium text-white text-center transition-colors"
                data-testid="end-cta-guide-link"
              >
                {ctaGuide.title} - ${ctaGuide.price}
              </Link>
              <Link
                to="/glp-survival-system"
                className="px-4 py-2.5 bg-[#E07A5F] hover:bg-[#D4694E] rounded-xl text-sm font-medium text-white text-center transition-colors inline-flex items-center justify-center gap-1.5"
                data-testid="end-cta-bundle-link"
              >
                All 8 Guides - $49 <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        )}
        {!topicCTA && ctaGuide && (
          <div className="pt-4 border-t border-white/20">
            <p className="text-sm text-white/70 mb-3">
              If you want a structured, step-by-step approach to this topic:
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to={`/guides/${ctaGuide.slug}`}
                className="px-4 py-2.5 bg-white/15 hover:bg-white/25 rounded-xl text-sm font-medium text-white text-center transition-colors"
                data-testid="end-cta-guide-link"
              >
                {ctaGuide.title} - ${ctaGuide.price}
              </Link>
              <Link
                to="/glp-survival-system"
                className="px-4 py-2.5 bg-[#E07A5F] hover:bg-[#D4694E] rounded-xl text-sm font-medium text-white text-center transition-colors inline-flex items-center justify-center gap-1.5"
                data-testid="end-cta-bundle-link"
              >
                All 8 Guides - $49 <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Operating System CTA */}
      <div className="mt-6 p-5 bg-white rounded-2xl border border-[#E8E4DC]" data-testid="os-article-cta">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#E8F0E4] flex items-center justify-center flex-shrink-0">
            <ArrowRight className="w-5 h-5 text-[#2D5A3D]" />
          </div>
          <div>
            <h3 className="font-semibold text-base mb-1 text-[#1A2018]">Follow the full system</h3>
            <p className="text-sm text-[#4B5548] mb-3">The GLP-1 Operating System walks you through every phase — from Week 1 through long-term optimization.</p>
            <Link to="/glp1-operating-system" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D5A3D] hover:text-[#1B4332] transition-colors" data-testid="os-link">
              See the Operating System <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Skool Community CTA */}
      <div className="mt-6 p-6 bg-[#F2F0E9] rounded-2xl border border-[#D4A373]/30" data-testid="skool-article-cta">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-[#D4A373] flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-lg mb-1 text-[#1A2018]">Want to apply this with other GLP-1 users?</h3>
            <p className="text-sm text-[#4B5548] mb-3">
              The GLP-1 Lab OS community runs the system daily — join free and complete your first check-in today.
            </p>
            <a
              href={SKOOL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#D4A373] hover:bg-[#C4936A] rounded-xl text-sm font-medium text-white transition-colors"
              data-testid="skool-community-link"
            >
              Join the Community <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
